import React, {useEffect} from 'react';
import {Spinner} from 'react-bootstrap';
import {toast} from 'react-toastify';
import {DownloadingTwoTone, SwapVertTwoTone} from '@mui/icons-material';
import {useShipments} from '../application/useShipments';
import {RemoteDataUI} from '../../common/ui/RemoteDataUI';
import {Carritos, TableFactura} from './cuenta.elements';
import {spanishDateFormatter} from '../../ui/components/SpanishDateFormatter';
import {downloadBlob} from '../../helpers/downloadBlob';
import {fetchPdfShipment} from '../infrastructure/ShipmentApi';
import {Indicador} from '../../ui';
import {DateRangePicker} from '../../common/ui/DateRangePicker';
import {isFailure, isPending} from '../../common/domain/RemoteData.model';

const pdfFileName = (number) => `albaran-${number.replace(/[^A-Za-z0-9._-]/g, '-')}.pdf`;

/** Historial de albaranes (BC) del cliente, con descarga del PDF. Misma estructura que Facturas. */
export const Shipments = () => {
    const {
        shipmentsRemoteData,
        toggleSortOrder,
        dateRange,
        setDateRange,
        downloadShipments,
        bulkDownloadRemoteData,
        actingAsNumCliente
    } = useShipments();

    const download = async (number) => {
        try {
            toast.info(`Descargando albarán ${number}.`);
            downloadBlob(await fetchPdfShipment(number, actingAsNumCliente), pdfFileName(number));
        } catch (error) {
            console.error(JSON.stringify(error));
            toast.error(`Error en la descarga del albarán ${number}.`);
        }
    };

    useEffect(() => {
        if (isPending(bulkDownloadRemoteData)) {
            toast.info('Descargando albaranes.');
        }
        if (isFailure(bulkDownloadRemoteData)) {
            console.error(JSON.stringify(bulkDownloadRemoteData.error));
            toast.error('Error en la descarga de los albaranes');
        }
    }, [bulkDownloadRemoteData]);

    return (
        <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between">
                <Indicador indicador={'Historial de albaranes'} />
                <div>
                    <DateRangePicker defaultDates={dateRange} onDateRangeChange={setDateRange} />
                </div>
            </div>
            <RemoteDataUI remoteData={shipmentsRemoteData}>
                <RemoteDataUI.Pending>
                    <div className="d-flex justify-content-center">
                        <Spinner />
                    </div>
                </RemoteDataUI.Pending>

                <RemoteDataUI.Success>
                    {
                        shipments => shipments.length > 0 ? (
                            <Carritos className="row gap-3">
                                <div className="col-12">
                                    <button className="btn btn-primary" type="button"
                                            disabled={isPending(bulkDownloadRemoteData)}
                                            onClick={() => downloadShipments(shipments)}>
                                        <div className="d-flex flex-row align-items-center gap-2">
                                            <span>Descargar albaranes</span>
                                            {isPending(bulkDownloadRemoteData) && <Spinner size="sm" />}
                                        </div>
                                    </button>
                                </div>
                                <div className="col-xl-12">
                                    <TableFactura>
                                        <thead>
                                        <tr>
                                            <th>Nº de albarán</th>
                                            <th className="d-flex flex-row align-items-center gap-2">
                                                <span>Fecha</span>
                                                <SwapVertTwoTone
                                                    className="opacity-25"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => toggleSortOrder()}
                                                />
                                            </th>
                                            <th>Pedido</th>
                                            <th>Entregado en</th>
                                            <th>Descargar</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {
                                            shipments.map((shipment) => (
                                                <tr className="align-middle" key={shipment.id}>
                                                    <td>
                                                        <h5 className="m-0">{shipment.number}</h5>
                                                    </td>
                                                    <td>
                                                        <span className="text-primary">
                                                          {spanishDateFormatter(shipment.postingDate)}
                                                        </span>
                                                    </td>
                                                    <td>{shipment.orderNumber ?? '—'}</td>
                                                    <td>
                                                        {[shipment.shipToName, shipment.shipToCity].filter(Boolean).join(' · ') || '—'}
                                                    </td>
                                                    <td>
                                                        <DownloadingTwoTone
                                                            className="text-primary me-3"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => download(shipment.number)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                        </tbody>
                                    </TableFactura>
                                </div>
                            </Carritos>
                        ) : (
                            <div className="alert alert-light">
                                No se encontraron albaranes para las fechas seleccionadas. Por favor, pruebe con otro rango de fechas.
                            </div>
                        )
                    }
                </RemoteDataUI.Success>

                <RemoteDataUI.Failure>
                    <div className="alert alert-danger">
                        <p>No es posible cargar los albaranes. Por favor, reintente más tarde o contacte con nosotros.</p>
                    </div>
                </RemoteDataUI.Failure>
            </RemoteDataUI>
        </div>
    );
};
